import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Modal,
    View,
    Text,
    TouchableOpacity,
    StatusBar,
    Alert,
    Image,
    StyleSheet,
    Animated,
    Easing,
    useWindowDimensions,
    ScrollView,
    KeyboardAvoidingView,
    Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useMutation, useConvex } from 'convex/react';
import { api } from '../convex/_generated/api';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import * as Location from 'expo-location';
import { colors, fontFamily } from '../constants/theme';
import { calculateDistance } from '../utils/googlePlaces';

/** RN-web: `Alert` often does not appear above a full-screen `Modal`; use the browser dialog on web. */
function passModalAlert(title, message) {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.alert(message ? `${title}\n\n${message}` : title);
        return;
    }
    Alert.alert(title, message);
}

/** ~40m on-site gate for pass redemption (matches server `PASS_REDEEM_MAX_DISTANCE_METERS` and check-in radius). */
const PASS_REDEEM_AT_VENUE_RADIUS_KM = 40 / 1000;

function getEligibleRestaurantIds(pass) {
    if (!pass?.restaurant) return [];
    if (pass.eligibleRestaurantIds?.length) return pass.eligibleRestaurantIds;
    return [pass.restaurant];
}

function getRestaurantDocForEligibleId(pass, id) {
    const d = pass.eligibleRestaurantDetails?.find((r) => String(r._id) === String(id));
    if (d) return d;
    if (pass.restaurantDetails && String(pass.restaurantDetails._id) === String(id)) {
        return pass.restaurantDetails;
    }
    return null;
}

function pickNearestEligibleWithinRadiusKm(lat, lng, pass, maxKm) {
    const ids = getEligibleRestaurantIds(pass);
    let best = null;
    let bestKm = Infinity;
    for (const id of ids) {
        const d = getRestaurantDocForEligibleId(pass, id);
        if (!d?.latitude || !d?.longitude) continue;
        const km = calculateDistance(lat, lng, d.latitude, d.longitude);
        if (km <= maxKm && km < bestKm) {
            bestKm = km;
            best = id;
        }
    }
    return best;
}

/**
 * Pick redemption venue from fresh GPS only: nearest eligible restaurant within radius.
 * Returns null if location is unavailable or user is not within range of any eligible venue.
 */
function resolveRedemptionVenueId({ pass, lat, lng }) {
    const eligibleIds = getEligibleRestaurantIds(pass);
    if (!eligibleIds.length) return pass?.restaurant ?? null;
    if (lat == null || lng == null) return null;
    return pickNearestEligibleWithinRadiusKm(lat, lng, pass, PASS_REDEEM_AT_VENUE_RADIUS_KM);
}

const styles = StyleSheet.create({
    /** Do not use overflow: 'hidden' here — it clips the pass card and redeem button on tall layouts. */
    safe: { flex: 1, backgroundColor: colors.cream },
    main: {
        width: '100%',
        zIndex: 1,
        padding: 24,
        paddingTop: 8,
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 32,
        width: '100%',
        maxWidth: 384,
    },
    headerTextCol: { flex: 1 },
    headerTitle: {
        fontSize: 30,
        fontFamily: fontFamily.abhayaBold,
        color: colors.brownDark,
        marginBottom: 8,
        textAlign: 'center',
    },
    headerSub: {
        fontSize: 18,
        fontFamily: fontFamily.abhaya,
        color: colors.disgoBrown,
        textAlign: 'center',
    },
    closeRound: {
        width: 40,
        height: 40,
        backgroundColor: colors.disgoBrown,
        borderRadius: 20,
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: 16,
    },
    card: {
        backgroundColor: colors.white,
        borderRadius: 16,
        marginBottom: 24,
        width: '100%',
        maxWidth: 384,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
        elevation: 8,
        overflow: 'hidden',
    },
    cardInner: { padding: 24, paddingBottom: 0 },
    logoAbs: { position: 'absolute', top: 24, right: 24 },
    logoStack: { alignItems: 'center' },
    logoImg: { width: 64, height: 64, marginBottom: 0 },
    passLabel: {
        fontSize: 14,
        fontFamily: fontFamily.abhaya,
        color: colors.brownDark,
        marginTop: -32,
        marginLeft: 16,
    },
    passTitle: {
        fontSize: 24,
        fontFamily: fontFamily.abhayaBold,
        color: colors.brownDark,
        marginBottom: 24,
        paddingRight: 96,
    },
    detailBlock: { marginBottom: 16 },
    venuesSectionTitle: {
        fontSize: 16,
        fontFamily: fontFamily.abhayaBold,
        color: colors.brownDark,
        marginBottom: 10,
    },
    venuesScroll: { maxHeight: 176, marginBottom: 4 },
    venueRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginBottom: 10,
        paddingRight: 8,
    },
    venueHereHint: {
        fontSize: 14,
        fontFamily: fontFamily.abhaya,
        color: colors.disgoBrown,
        marginTop: 2,
    },
    detailText: { fontSize: 18, fontFamily: fontFamily.abhayaBold, color: colors.disgoBrown, marginBottom: 8 },
    sep: { height: 1, backgroundColor: colors.accentTan2, marginBottom: 16 },
    tsEnd: { alignItems: 'flex-end', marginBottom: 16 },
    tsText: { fontSize: 18, fontFamily: fontFamily.abhaya, color: colors.disgoBrown },
    redeemBtn: {
        backgroundColor: colors.disgoBrown,
        borderBottomLeftRadius: 16,
        borderBottomRightRadius: 16,
        paddingVertical: 16,
        paddingHorizontal: 32,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
    },
    redeemText: { color: colors.white, fontSize: 20, fontFamily: fontFamily.abhayaBold, marginRight: 12 },
    timerBar: {
        backgroundColor: colors.accentTan2,
        borderBottomLeftRadius: 16,
        borderBottomRightRadius: 16,
        paddingHorizontal: 24,
        paddingVertical: 12,
    },
    timerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    timerText: { fontSize: 20, fontFamily: fontFamily.abhayaBold, color: colors.brownDark },
    calCircle: {
        width: 32,
        height: 32,
        backgroundColor: colors.white,
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
    },
    checkinBox: {
        backgroundColor: colors.accentTan2,
        borderRadius: 16,
        padding: 24,
        marginBottom: 24,
        width: '100%',
        maxWidth: 384,
    },
    checkinTitle: {
        fontSize: 18,
        fontFamily: fontFamily.abhayaBold,
        color: colors.brownDark,
        textAlign: 'center',
        marginBottom: 16,
    },
    checkinBody: {
        fontSize: 14,
        fontFamily: fontFamily.abhaya,
        color: colors.disgoBrown,
        textAlign: 'center',
        marginBottom: 24,
    },
    checkinRow: { flexDirection: 'row', gap: 12 },
    checkinBtn: { flex: 1, borderRadius: 8, paddingHorizontal: 16, paddingVertical: 12 },
    checkinBtnText: { fontFamily: fontFamily.abhayaBold, textAlign: 'center', color: colors.black },
    postAct: { alignItems: 'center', width: '100%', maxWidth: 384 },
    postTitle: { fontSize: 20, fontFamily: fontFamily.abhayaBold, color: colors.brownDark, marginBottom: 8 },
    postBody: { fontSize: 20, fontFamily: fontFamily.abhaya, color: colors.brownDark, marginBottom: 16 },
    timerHuge: { fontSize: 36, fontFamily: fontFamily.abhayaBold, color: colors.brownDark },
});

const PassUseModal = ({ isOpen, onClose, pass, selectedRestaurant, getUserMembership, userDoc }) => {
    const convex = useConvex();
    const insets = useSafeAreaInsets();

    /** Ids where this pass can be redeemed (primary + optional chain). */
    const eligibleRestaurantIds = useMemo(() => {
        if (!pass?.restaurant) return [];
        if (pass.eligibleRestaurantIds?.length) return pass.eligibleRestaurantIds;
        return [pass.restaurant];
    }, [pass?.restaurant, pass?.eligibleRestaurantIds]);

    const resolveRedemptionVenueIdAsync = useCallback(async () => {
        if (!pass || !userDoc?._id) return null;
        const eligibleIds = getEligibleRestaurantIds(pass);
        if (!eligibleIds.length) return pass.restaurant;

        let lat = null;
        let lng = null;
        try {
            const { status } = await Location.requestForegroundPermissionsAsync();
            if (status === 'granted') {
                const p = await Location.getCurrentPositionAsync({});
                lat = p.coords.latitude;
                lng = p.coords.longitude;
            }
        } catch (_) {
            /* location unavailable */
        }

        return resolveRedemptionVenueId({ pass, lat, lng });
    }, [pass, userDoc?._id]);

    /** Resolved venue from fresh GPS: nearest eligible within check-in radius (~40m). */
    const [redeemVenueId, setRedeemVenueId] = useState(null);
    /** Locked to the same `venueId` from `resolveRedemptionVenueIdAsync` when Redeem opens check-in — aligns status + UI with 40m rule. */
    const [checkInTargetVenueId, setCheckInTargetVenueId] = useState(null);

    useEffect(() => {
        if (!isOpen || !pass?._id || !userDoc?._id) {
            if (!isOpen) {
                setRedeemVenueId(null);
                setCheckInTargetVenueId(null);
            }
            return undefined;
        }
        let cancelled = false;
        (async () => {
            const id = await resolveRedemptionVenueIdAsync();
            if (!cancelled) setRedeemVenueId(id);
        })();
        return () => {
            cancelled = true;
        };
    }, [isOpen, pass?._id, userDoc?._id, resolveRedemptionVenueIdAsync]);

    /** Doc for display, check-in, and membership — prefer enriched eligible list. */
    const redeemVenue = useMemo(() => {
        if (!pass || !redeemVenueId) {
            return pass?.restaurantDetails ?? selectedRestaurant;
        }
        const fromEligible = pass.eligibleRestaurantDetails?.find(
            (r) => String(r._id) === String(redeemVenueId)
        );
        if (fromEligible) return fromEligible;
        if (String(pass.restaurantDetails?._id) === String(redeemVenueId)) {
            return pass.restaurantDetails;
        }
        if (String(selectedRestaurant?._id) === String(redeemVenueId)) {
            return selectedRestaurant;
        }
        return pass.restaurantDetails ?? selectedRestaurant;
    }, [pass, redeemVenueId, selectedRestaurant]);

    /** Ordered rows for UI: primary first, then extras (matches `eligibleRestaurantIds`). */
    const eligibleVenuesDisplay = useMemo(() => {
        if (!pass?.restaurant) return [];
        return eligibleRestaurantIds.map((id) => {
            let doc = pass.eligibleRestaurantDetails?.find(
                (r) => String(r._id) === String(id)
            );
            if (!doc && String(pass.restaurantDetails?._id) === String(id)) {
                doc = pass.restaurantDetails;
            }
            if (!doc && String(selectedRestaurant?._id) === String(id)) {
                doc = selectedRestaurant;
            }
            return {
                id: String(id),
                name: doc?.name || 'Restaurant',
                isCurrentVenue:
                    String(id) === String(checkInTargetVenueId ?? redeemVenueId),
            };
        });
    }, [pass, eligibleRestaurantIds, redeemVenueId, checkInTargetVenueId, selectedRestaurant]);

    const { width: winW, height: winH } = useWindowDimensions();
    const showMultiVenueList = eligibleVenuesDisplay.length > 1;
    /** Large, quicker drift + wobble so staff can spot live motion at a glance (not a screenshot). */
    const bgDrift = useRef(new Animated.ValueXY({ x: 0, y: 0 })).current;
    const bgSlantWobble = useRef(new Animated.Value(0)).current;
    /** Web: fresh `getCurrentPosition` after check-in sometimes fails; allow redeem with same fix briefly. */
    const recentCheckInLocationRef = useRef(null);
    const bgMotion = useMemo(() => {
        const ax = Math.min(winW * 0.4, 180);
        const ay = Math.min(winH * 0.28, 160);
        return { ax, ay };
    }, [winW, winH]);
    const slantRotate = bgSlantWobble.interpolate({
        inputRange: [0, 1],
        outputRange: ['10deg', '34deg'],
    });

    const [timer, setTimer] = useState(120); // 2 minutes in seconds
    const [isActivated, setIsActivated] = useState(false);
    const [activationTime, setActivationTime] = useState(null);
    const [timestamp, setTimestamp] = useState(null);
    const [showCheckinPrompt, setShowCheckinPrompt] = useState(false);
    const [isCheckingIn, setIsCheckingIn] = useState(false);
    const usePassMutation = useMutation(api.passActions.usePass);
    const claimPassMutation = useMutation(api.passActions.claimPass);
    const checkInMutation = useMutation(api.checkInWithLocation.checkInWithLocation);
    /** Private, photo-less moment so recap reminders can nudge after Disgo pass check-in only. */
    const createMomentMutation = useMutation(api.moments.createMoment);

    // Get membership data to access tier information
    const membership = getUserMembership ? getUserMembership(redeemVenue?.name) : null;


    /** Venue for copy / Convex — locked 40m target during check-in flow, else live resolve from open/GPS. */
    const venueIdForCheckInStatus = checkInTargetVenueId ?? redeemVenueId;

    // Note: do not gate the check-in strip on useQuery(hasCheckedInToday). It can lag or disagree with the
    // one-shot convex.query in handleUsePass and hide "Check in" even when the user still needs to check in.

    const venueNameForCheckInPrompt = useMemo(() => {
        const id = venueIdForCheckInStatus;
        if (!pass || !id) {
            return redeemVenue?.name || "this restaurant";
        }
        const doc = getRestaurantDocForEligibleId(pass, id);
        return doc?.name || redeemVenue?.name || "this restaurant";
    }, [pass, venueIdForCheckInStatus, redeemVenue?.name]);



    // Format timestamp as MM.DD.YYYY @HH:MM
    const formatTimestamp = (date) => {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'pm' : 'am';
        const displayHours = hours % 12 || 12;
        const displayMinutes = minutes.toString().padStart(2, '0');
        
        return {
            date: `${month}.${day}.${year}`,
            time: `@${displayHours}:${displayMinutes}${ampm}`
        };
    };

    // Format timer as MM:SS
    const formatTimer = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    // Format time range
    const formatTimeRange = (startTime, endTime) => {
        if (!startTime || !endTime) return '12am-2am';
        
        // Convert Unix timestamps to readable time strings
        const startTimeStr = new Date(startTime * 1000).toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
        });
        const endTimeStr = new Date(endTime * 1000).toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
        });
        
        return `${startTimeStr}-${endTimeStr}`;
    };

    // Format pass date for display
    const formatPassDate = (startTime) => {
        if (!startTime) return '';
        
        const startDate = new Date(startTime * 1000);
        return startDate.toLocaleDateString('en-US', { 
            weekday: 'long',
            month: 'long',
            day: 'numeric'
        });
    };

    /** Shared: claim + usePass after user confirms (native Alert vs web `confirm` — RN-web Alert is often hidden behind this Modal). */
    const redeemPassAfterConfirm = async (userId, venueId) => {
        if (!pass?._id || !userId || !venueId) return;
        try {
            try {
                const claimResult = await claimPassMutation({
                    passId: pass._id,
                    userId,
                    actionType: "down",
                });
                if (!claimResult.success) {
                    /* may already be claimed */
                }
            } catch {
                /* continue */
            }

            let pos;
            try {
                pos = await Location.getCurrentPositionAsync({});
            } catch {
                const fix = recentCheckInLocationRef.current;
                if (
                    Platform.OS === "web" &&
                    fix &&
                    Date.now() - fix.t < 120000
                ) {
                    pos = {
                        coords: {
                            latitude: fix.lat,
                            longitude: fix.lng,
                        },
                    };
                } else {
                    passModalAlert(
                        "Location needed",
                        "We need a fresh location fix to confirm you're at the restaurant to redeem this discount."
                    );
                    return;
                }
            }
            const result = await usePassMutation({
                passId: pass._id,
                userId,
                redeemAtRestaurantId: venueId,
                redeemLatitude: pos.coords.latitude,
                redeemLongitude: pos.coords.longitude,
            });

            if (result?.success) {
                setIsActivated(true);
                setActivationTime(new Date());
                setTimestamp(formatTimestamp(new Date()));
                setTimer(120);
            } else {
                console.error("Failed to activate pass:", result?.error);
                passModalAlert(
                    "Error",
                    result?.error || "Failed to activate pass. Please try again."
                );
            }
        } catch (error) {
            console.error("Failed to activate pass:", error);
            passModalAlert("Error", "Failed to activate pass. Please try again.");
        }
    };

    const confirmAndUsePass = (userId, venueId) => {
        if (!pass?._id || !userId || !venueId) return;

        const message = `Are you sure you want to use "${pass.title}"? This action cannot be undone.`;

        if (Platform.OS === "web" && typeof window !== "undefined") {
            const ok = window.confirm(`Use Pass\n\n${message}`);
            if (!ok) return;
            void redeemPassAfterConfirm(userId, venueId);
            return;
        }

        Alert.alert("Use Pass", message, [
            { text: "Cancel", style: "cancel" },
            {
                text: "Use Pass",
                style: "destructive",
                onPress: () => {
                    void redeemPassAfterConfirm(userId, venueId);
                },
            },
        ]);
    };

    const handleUsePass = async () => {
        if (!pass?._id || !userDoc?._id) return;

        const venueId = await resolveRedemptionVenueIdAsync();
        setRedeemVenueId(venueId);

        if (!venueId) {
            passModalAlert(
                "Can't redeem here yet",
                "You need to be at one of the restaurants listed on this pass to use it."
            );
            return;
        }

        const userId = userDoc._id;

        const checkedIn = await convex.query(api.checkInWithLocation.hasCheckedInToday, {
            userId,
            restaurantId: venueId,
        });

        if (checkedIn !== true) {
            setCheckInTargetVenueId(venueId);
            setShowCheckinPrompt(true);
            return;
        }

        confirmAndUsePass(userId, venueId);
    };

    const handleCheckin = async () => {
        const uid = userDoc._id;
        const restaurantId = checkInTargetVenueId ?? redeemVenueId;
        if (!uid || !restaurantId) {
            passModalAlert("Error", "Unable to check in. Please try again.");
            return;
        }

        setIsCheckingIn(true);
        try {
            // Get current location from the user's device
            const { status } = await Location.requestForegroundPermissionsAsync();
            if (status !== 'granted') {
                passModalAlert('Permission to access location was denied', 'Enable location in your browser to check in and redeem.');
                setIsCheckingIn(false);
                return;
            }

            const location = await Location.getCurrentPositionAsync({});
            recentCheckInLocationRef.current = {
                lat: location.coords.latitude,
                lng: location.coords.longitude,
                t: Date.now(),
            };
            const result = await checkInMutation({
                userId: uid,
                restaurantId,
                latitude: location.coords.latitude,
                longitude: location.coords.longitude,
            });

            if (result.success) {
                const privyDid = userDoc?.privyDid;
                if (privyDid) {
                    try {
                        await createMomentMutation({
                            privyDid,
                            restaurantId,
                            visibility: "only_me",
                            checkInLatitude: location.coords.latitude,
                            checkInLongitude: location.coords.longitude,
                        });
                    } catch (momentErr) {
                        console.error(
                            "Pass check-in: could not create private moment for recap:",
                            momentErr
                        );
                    }
                }
                setCheckInTargetVenueId(null);
                setShowCheckinPrompt(false);
                const refreshed = await resolveRedemptionVenueIdAsync();
                setRedeemVenueId(refreshed);
                const venueAfter = refreshed ?? restaurantId;
                /** Chain: check-in → same confirmation as Redeem when already checked in. */
                confirmAndUsePass(uid, venueAfter);
            } else {
                if (result.errorType === "location") {
                    passModalAlert(
                        "Location Check Failed",
                        "You must be at the restaurant location to check in. Please get closer to the restaurant and try again."
                    );
                } else {
                    passModalAlert("Check-in Failed", result.errorMessage || "Unable to check in. Please try again.");
                }
            }
        } catch (error) {
            console.error("Error during check-in:", error);
            passModalAlert("Error", "Failed to check in. Please try again.");
        } finally {
            setIsCheckingIn(false);
        }
    };

    const handleCloseCheckinPrompt = () => {
        setShowCheckinPrompt(false);
        setCheckInTargetVenueId(null);
    };

    const handleClose = () => {
        onClose();
        setIsActivated(false);
        setActivationTime(null);
        setTimestamp(null);
        setTimer(120);
        setShowCheckinPrompt(false);
        setCheckInTargetVenueId(null);
        setIsCheckingIn(false);
        setRedeemVenueId(null);
        recentCheckInLocationRef.current = null;
    };

    useEffect(() => {
        if (!isOpen) return undefined;
        const { ax, ay } = bgMotion;
        const driftLoop = Animated.loop(
            Animated.sequence([
                Animated.timing(bgDrift, {
                    toValue: { x: ax, y: -ay * 0.78 },
                    duration: 2000,
                    easing: Easing.inOut(Easing.sin),
                    useNativeDriver: true,
                }),
                Animated.timing(bgDrift, {
                    toValue: { x: -ax * 0.92, y: ay },
                    duration: 2200,
                    easing: Easing.inOut(Easing.sin),
                    useNativeDriver: true,
                }),
                Animated.timing(bgDrift, {
                    toValue: { x: ax * 0.55, y: ay * 0.42 },
                    duration: 1800,
                    easing: Easing.inOut(Easing.sin),
                    useNativeDriver: true,
                }),
                Animated.timing(bgDrift, {
                    toValue: { x: 0, y: 0 },
                    duration: 1600,
                    easing: Easing.inOut(Easing.sin),
                    useNativeDriver: true,
                }),
            ])
        );
        const wobbleLoop = Animated.loop(
            Animated.sequence([
                Animated.timing(bgSlantWobble, {
                    toValue: 1,
                    duration: 1100,
                    easing: Easing.inOut(Easing.sin),
                    useNativeDriver: true,
                }),
                Animated.timing(bgSlantWobble, {
                    toValue: 0,
                    duration: 1100,
                    easing: Easing.inOut(Easing.sin),
                    useNativeDriver: true,
                }),
            ])
        );
        driftLoop.start();
        wobbleLoop.start();
        return () => {
            driftLoop.stop();
            wobbleLoop.stop();
        };
    }, [isOpen, bgDrift, bgMotion, bgSlantWobble]);

    // Timer effect
    useEffect(() => {
        if (isActivated && timer > 0) {
            const interval = setInterval(() => {
                setTimer((prevTimer) => {
                    if (prevTimer <= 1) {
                        // Auto-close when timer reaches 0
                        setTimeout(() => handleClose(), 1000);
                        return 0;
                    }
                    return prevTimer - 1;
                });
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [isActivated, timer]);

    if (!isOpen) return null;

    return (
        <Modal
            animationType="fade"
            transparent={false}
            visible={isOpen}
            onRequestClose={handleClose}
        >
            <StatusBar barStyle="dark-content" backgroundColor={colors.cream} />
            <SafeAreaView style={styles.safe} edges={['left', 'right']}>
                <Animated.View
                    pointerEvents="none"
                    accessibilityElementsHidden
                    importantForAccessibility="no-hide-descendants"
                    style={[
                        StyleSheet.absoluteFillObject,
                        { overflow: 'hidden', transform: bgDrift.getTranslateTransform() },
                    ]}
                >
                    <View
                        style={{
                            position: 'absolute',
                            width: winW * 1.4,
                            height: winW * 1.4,
                            borderRadius: winW * 0.7,
                            backgroundColor: colors.disgoBrown,
                            opacity: 0.13,
                            top: winH * -0.08,
                            left: winW * -0.22,
                        }}
                    />
                    <View
                        style={{
                            position: 'absolute',
                            width: winW * 1.15,
                            height: winW * 1.15,
                            borderRadius: winW * 0.58,
                            backgroundColor: colors.yellow400,
                            opacity: 0.1,
                            bottom: winH * -0.14,
                            right: winW * -0.22,
                        }}
                    />
                    <Animated.View
                        style={{
                            position: 'absolute',
                            width: winW * 0.65,
                            height: winH * 0.45,
                            borderRadius: 20,
                            backgroundColor: colors.brownDark,
                            opacity: 0.08,
                            transform: [{ rotate: slantRotate }],
                            top: winH * 0.28,
                            left: winW * -0.1,
                        }}
                    />
                </Animated.View>
                <KeyboardAvoidingView
                    style={{ flex: 1 }}
                    behavior={Platform.OS === 'ios' ? 'padding' : undefined}
                    keyboardVerticalOffset={Platform.OS === 'ios' ? 24 : 0}
                >
                <ScrollView
                    contentContainerStyle={{
                        paddingTop: Math.max(insets.top, 12) + 4,
                        paddingBottom: Math.max(insets.bottom, 16) + 32,
                        alignItems: 'center',
                    }}
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator={false}
                    nestedScrollEnabled
                >
                <View style={styles.main}>
                    {/* Header with close button */}
                    <View style={styles.headerRow}>
                        <View style={styles.headerTextCol}>
                            <Text style={styles.headerTitle}>
                                Redeem your pass!
                            </Text>
                            <Text style={styles.headerSub}>
                                Show this screen to your server to redeem your perk.
                            </Text>
                        </View>
                        <TouchableOpacity 
                            onPress={handleClose}
                            style={styles.closeRound}
                        >
                            <Ionicons name="close" size={20} color="white" />
                        </TouchableOpacity>
                    </View>

                    {/* Main Pass Card */}
                    <View style={styles.card}>
                        {/* Card Content */}
                        <View style={styles.cardInner}>
                            {/* DISGO Pass Logo */}
                            <View style={styles.logoAbs}>
                                <View style={styles.logoStack}>
                                    <Image 
                                        source={require('../assets/DisgoLogoBlack.png')}
                                        style={styles.logoImg}
                                        resizeMode="contain"
                                    />
                                    <Text style={styles.passLabel}>
                                        Pass
                                    </Text>
                                </View>
                            </View>

                            {/* Main Offer */}
                            <Text style={styles.passTitle}>
                                {pass.title || "Special Offer"}
                            </Text>

                            {/* Restaurant / eligible locations */}
                            <View style={styles.detailBlock}>
                                {showMultiVenueList ? (
                                    <>
                                        <Text style={styles.venuesSectionTitle}>
                                            Locations you can use this pass
                                        </Text>
                                        <ScrollView
                                            style={styles.venuesScroll}
                                            nestedScrollEnabled
                                            keyboardShouldPersistTaps="handled"
                                            showsVerticalScrollIndicator={eligibleVenuesDisplay.length > 4}
                                        >
                                            {eligibleVenuesDisplay.map((row) => (
                                                <View key={row.id} style={styles.venueRow}>
                                                    <Ionicons
                                                        name="location-outline"
                                                        size={18}
                                                        color={colors.disgoBrown}
                                                        style={{ marginRight: 8, marginTop: 2 }}
                                                    />
                                                    <View style={{ flex: 1 }}>
                                                        <Text style={[styles.detailText, { marginBottom: 0 }]}>
                                                            {row.name}
                                                        </Text>
                                                        {row.isCurrentVenue ? (
                                                            <Text style={styles.venueHereHint}>
                                                                Check in & redeem here
                                                            </Text>
                                                        ) : null}
                                                    </View>
                                                </View>
                                            ))}
                                        </ScrollView>
                                    </>
                                ) : (
                                    <Text style={styles.detailText}>
                                        {redeemVenue?.name || 'Restaurant'}
                                    </Text>
                                )}
                                {pass.startTime && (
                                    <Text style={[styles.detailText, showMultiVenueList ? { marginTop: 12 } : null]}>
                                        {formatPassDate(pass.startTime)} • {formatTimeRange(pass.startTime, pass.endTime)}
                                    </Text>
                                )}
                                <Text style={[styles.detailText, { marginBottom: 0 }]}>
                                    Loyalty Tiers • {pass.tierRequired || membership?.tier || ''}
                                </Text>
                            </View>

                            {/* Separator Line */}
                            <View style={styles.sep} />

                            {/* Timestamp */}
                            {timestamp && (
                                <View style={styles.tsEnd}>
                                    <Text style={styles.tsText}>
                                        Timestamp: {timestamp.date} {timestamp.time}
                                    </Text>
                                </View>
                            )}
                        </View>

                        {/* Check-in required: same slot as redeem so it stays on screen (not below a tall card). */}
                        {!isActivated && showCheckinPrompt && (
                            <View
                                style={[
                                    styles.checkinBox,
                                    {
                                        marginBottom: 0,
                                        width: '100%',
                                        maxWidth: '100%',
                                        borderRadius: 0,
                                        borderBottomLeftRadius: 16,
                                        borderBottomRightRadius: 16,
                                    },
                                ]}
                            >
                                <Text style={styles.checkinTitle}>
                                    Check-in Required
                                </Text>
                                <Text style={styles.checkinBody}>
                                    Check in at {venueNameForCheckInPrompt}, then you'll confirm using your pass. Make sure you're at the restaurant location.
                                </Text>
                                <View style={styles.checkinRow}>
                                    <TouchableOpacity
                                        onPress={handleCloseCheckinPrompt}
                                        style={[styles.checkinBtn, { backgroundColor: colors.gray400 }]}
                                    >
                                        <Text style={styles.checkinBtnText}>
                                            Cancel
                                        </Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        onPress={handleCheckin}
                                        disabled={isCheckingIn}
                                        style={[
                                            styles.checkinBtn,
                                            { backgroundColor: isCheckingIn ? colors.gray400 : colors.saddle },
                                        ]}
                                    >
                                        <Text style={styles.checkinBtnText}>
                                            {isCheckingIn ? 'Checking In...' : 'Check In'}
                                        </Text>
                                    </TouchableOpacity>
                                </View>
                            </View>
                        )}

                        {/* Redeem Pass Button - Integrated at bottom of card */}
                        {!isActivated && !showCheckinPrompt && (
                            <TouchableOpacity
                                onPress={handleUsePass}
                                style={styles.redeemBtn}
                            >
                                <Text style={styles.redeemText}>
                                    Redeem Pass
                                </Text>
                                <Ionicons name="gift" size={24} color="white" />
                            </TouchableOpacity>
                        )}

                        {/* Timer Bar - Bottom of Card (when activated) */}
                        {isActivated && (
                            <View style={styles.timerBar}>
                                <View style={styles.timerRow}>
                                    <Text style={styles.timerText}>
                                        Expires in {formatTimer(timer)}
                                    </Text>
                                    <View style={styles.calCircle}>
                                        <Ionicons name="calendar" size={20} color={colors.disgoBrown} />
                                    </View>
                                </View>
                            </View>
                        )}
                    </View>

                    {/* Post-activation state - only show if not using timer bar */}
                    {isActivated && !timestamp && (
                        <View style={styles.postAct}>
                            <Text style={styles.postTitle}>
                                Timestamp:
                            </Text>
                            <Text style={styles.postBody}>
                                {new Date().toLocaleDateString()} @{new Date().toLocaleTimeString()}
                            </Text>
                            <Text style={styles.postTitle}>
                                expires in
                            </Text>
                            <Text style={styles.timerHuge}>
                                {formatTimer(timer)}
                            </Text>
                        </View>
                    )}
                </View>
                </ScrollView>
                </KeyboardAvoidingView>
            </SafeAreaView>
        </Modal>
    );
};

export default PassUseModal; 
